import React, { Fragment, useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Card,
  Select,
  Button,
  Dropdown,
  Menu,
  Affix,
  Popconfirm,
  Typography,
  Breadcrumb,
  Input,
  Space,
} from "antd";
import ListTable from "common/ListTable";
import BodyHeader from "../../Common/BodyHeader";
import Header from "../../Common/Header";
import { isMobileView } from "../../Common/AppHelper";
import { Link, useLocation } from "react-router-dom";
import {
  MoreOutlined,
  EditOutlined,
  DeleteTwoTone,
  EyeOutlined,
} from "@ant-design/icons";
import {
  STYLE_WISE_COST_LIST,
  STYLE_WISE_COST_DELETE,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, deleteData, postData } from "../../../apiServices/common";
import CustomFilter from "../../Common/CustomFilter";
import { alertPop } from "../../../apiServices/common/helper";
import { commaSeparateNumber, hasPermission } from "../../../utils/functions";
import { NO_PERMISSION_REQUIRED } from "../../../routes/permissions";

const CostEstimateEntry = (props) => {
  // Props
  const { width } = props;

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  //   States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [costList, setCostList] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rangeData, setRangeData] = useState(10);
  const activeFiltersRef = useRef({});

  // Round 2 (B, R3): mounted-ref + request-sequence guards — CostEstimateFilterFields
  // now also submits the form once on mount for the default sales contract.
  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const columns = [
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "sales_contract",
    },
    {
      title: "Contract Value",
      dataIndex: "contract_value",
      key: "contract_value",
      render: (value) => <span>{commaSeparateNumber(value)}</span>,
    },
    {
      title: "Total Value",
      dataIndex: "total_value",
      key: "total_value",
      render: (value) => <span>{commaSeparateNumber(value)}</span>,
    },
    {
      title: "Total With Adjust",
      dataIndex: "total_value_with_adjust",
      key: "total_value_with_adjust",
      render: (value) => <span>{commaSeparateNumber(value)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <span>{value === 0 ? "Pending" : "Approved"}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getAllCost = useCallback(async (filterValues = {}) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);

    const page = filterValues.page ?? currentPage;
    const bodyData = {
      page,
      per_page: filterValues?.per_page || 10,
      ...activeFiltersRef.current,
    };

    const response = await getData(STYLE_WISE_COST_LIST, false, bodyData);

    // Round 2 (B, R3): mounted guard + stale-response race guard (round-1 C1 pattern) —
    // this can now also be triggered by the mount-time default-selection in
    // CostEstimateFilterFields (via fetchCostList -> form.submit()).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response && response?.data?.code === 200) {
      const paginatedData = response?.data?.data;
      setCostList(
        paginatedData?.data?.map((item) => ({
          ...item,
          key: item.id,
        })) || [],
      );
      setTotalData(paginatedData?.total || 0);
      setRangeData(paginatedData?.per_page || 10);
    } else {
      setError("Could not load this list.");
      alertPop(
        "error",
        response?.messages ? response?.messages : "Something Went Wrong!",
      );
    }

    setLoading(false);
  }, [currentPage]);

  const fetchCostList = useCallback(
    (filterValues = {}) => {
      const isFilterCall = Object.keys(filterValues).some(
        (key) => !["page", "per_page"].includes(key),
      );

      if (isFilterCall) {
        const cleanedFilters = Object.fromEntries(
          Object.entries(filterValues).filter(
            ([, value]) => value != null && value !== "",
          ),
        );

        activeFiltersRef.current = cleanedFilters;

        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          getAllCost({ page: 1 });
        }
        return;
      }

      getAllCost(filterValues);
    },
    [getAllCost, currentPage],
  );

  useEffect(() => {
    getAllCost({ page: currentPage });
  }, [currentPage, getAllCost]);

  const removeCostEstimate = async (value) => {
    const response = await deleteData(`${STYLE_WISE_COST_DELETE}/${value}`);
    if (response && response?.code === 200) {
      alertPop("success", "Deleted Successfully!");
      getAllCost({ page: currentPage });
    } else {
      alertPop(
        "error",
        response?.data?.message
          ? response?.data?.message[0]
          : "Something Went Wrong!",
      );
    }
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/merchandising/cost-estimate-update/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
        {
          label: (
            <Link to={`/merchandising/cost-estimate-view/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
        {
          label: (
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => removeCostEstimate(data.id)}
            >
              <DeleteTwoTone /> Delete
            </Popconfirm>
          ),
          key: "2",
          disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
        },
      ]}
    />
  );

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const extra = (
    <Space>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Button key="3" size="small" type="primary">
        <Link to="/merchandising/cost-estimate-entry">Add New</Link>
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={costList}
        fetchTableData={fetchCostList}
        filterName="cost_estimate"
      />
    ),
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title={<Title level={5}>Cost Estimate</Title>}
            actions={extra}
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={error}
            onRetry={() => getAllCost({ page: currentPage })}
            dataSource={costList}
            columns={columns}
            rowKey="id"
            // Round 2 (B, R3): A3 empty-state hint — the mount-time default sales
            // contract (or a later manual one) returned no cost estimates.
            locale={{
              emptyText: "No cost estimates found for the selected Sales Contract.",
            }}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default CostEstimateEntry;
